import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarRange, Car, MapPin, AlertTriangle, Navigation } from "lucide-react";
import { STATUS_KEYS, STATUS_META } from "@/components/territory/statusMeta";

const hm = (minutes) => {
  const total = Math.round(minutes || 0);
  if (total < 60) return `${total}m`;
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
};

/** Monday to Friday, whole suburbs per day, ordered by who needs the call most. */
export const WeeklyPlannerView = ({ plan, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!plan) return null;
  if (!plan.days?.length) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground" data-testid="weekly-plan-empty">
          {plan.message || "Nothing to plan — no doctors with a known location in this filter."}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="weekly-plan">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Days used</p>
            <p className="text-2xl font-bold">{plan.summary.days}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Doctors</p>
            <p className="text-2xl font-bold">{plan.summary.doctors}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">With doctors</p>
            <p className="text-2xl font-bold">{hm(plan.summary.visit_minutes)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Driving</p>
            <p className="text-2xl font-bold">{hm(plan.summary.drive_minutes)}</p>
            <p className="text-[11px] text-muted-foreground">{plan.summary.drive_km}km</p>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {plan.source === "osrm" ? "road times via OSRM" : "estimated offline — OSRM unreachable"}
            </Badge>
            <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2" data-testid="weekly-plan-refresh">
              <CalendarRange className="w-4 h-4" />
              Rebuild
            </Button>
          </div>
        </CardContent>
      </Card>

      {plan.days.map((day) => (
        <Card key={day.day} data-testid={`weekly-plan-${day.day.toLowerCase()}`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">{day.day}</p>
              <span className="text-sm text-muted-foreground truncate">
                {day.stops.map((s) => s.suburb).join(" → ")}
              </span>
              <span className="flex-1" />
              <Badge variant="outline" className="text-[10px]">
                {day.doctors} doctors
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {hm(day.total_minutes)} incl. {hm(day.drive_minutes)} driving
              </Badge>
            </div>

            {day.stops.map((stop) => (
              <div key={stop.suburb} className="rounded-md border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary shrink-0" />
                  <p className="text-sm font-medium">{stop.suburb}</p>
                  <span className="text-xs text-muted-foreground">{stop.area || stop.city}</span>
                  {stop.drive_from_previous_minutes > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Car className="w-3 h-3" />
                      {hm(stop.drive_from_previous_minutes)} · {stop.drive_from_previous_km}km from the last stop
                    </span>
                  )}
                  <span className="flex-1" />
                  {STATUS_KEYS.filter((key) => stop.by_status[key]).map((key) => (
                    <Badge key={key} className={`text-[10px] hover:bg-inherit ${STATUS_META[key].chip}`}>
                      {stop.by_status[key]} {STATUS_META[key].short.toLowerCase()}
                    </Badge>
                  ))}
                </div>

                <div className="space-y-1.5">
                  {stop.doctors.map((doctor, index) => {
                    const meta = STATUS_META[doctor.status];
                    return (
                      <div
                        key={doctor.doctor_id}
                        className={`rounded border border-l-4 ${meta.accent} p-2 flex items-start gap-2`}
                        data-testid={`plan-doctor-${doctor.doctor_id}`}
                      >
                        <span className="text-xs font-semibold text-muted-foreground w-5 shrink-0 pt-0.5">
                          {index + 1}.
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium">{doctor.doctor_name}</p>
                            <Badge className={`text-[10px] hover:bg-inherit ${meta.chip}`}>{meta.short}</Badge>
                            <Badge variant="outline" className="text-[10px]">
                              {doctor.tier}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {doctor.address || "No street address on record"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {doctor.referrals_90days} referrals and{" "}
                            {doctor.visits_90days + doctor.appointments_90days} contacts in 90 days · last{" "}
                            {doctor.last_contact || "never"}
                            {doctor.next_doctor_km !== null && doctor.next_doctor && (
                              <span className="inline-flex items-center gap-1 ml-1">
                                <Navigation className="w-3 h-3" />
                                {doctor.next_doctor_km}km to {doctor.next_doctor}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {plan.unplanned?.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2" data-testid="weekly-plan-leftovers">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-semibold">
                {plan.unplanned.length} suburbs did not fit into five days
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {plan.unplanned.map((left) => (
                <Badge key={left.suburb} variant="outline" className="text-[10px]">
                  {left.suburb} · {left.doctors} doctors
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              They carry a lower priority score this week. Filter to one of them to plan it on its own.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeeklyPlannerView;
